<template>
    <div>
        <main-section-title>{{ i18n("views.DiscountEdit.UserGroups") }}:</main-section-title>

        <div class="form-group row">
            <form-label>{{ i18n("views.DiscountEdit.Operators") }}</form-label>
            <form-field>
                <SwitchInput v-model="operatorsEnabled" />
            </form-field>
        </div>

        <FadeTransition>
            <div class="form-group row" v-if="operatorsEnabled">
                <form-label>{{ i18n("views.DiscountEdit.Mode") }}</form-label>
                <form-field>
                    <EnumSelectBox
                        ref="operatorsModeSelect"
                        v-model="operatorsMode"
                        :enumDataSource="DiscountAccessMode"
                        enumTranslationNamespace="enums.DiscountAccessMode"
                        :optionsFilter="tempDiscountAccessModeOptionsFilterForOperators"
                    />
                </form-field>
            </div>
        </FadeTransition>

        <div class="form-group row">
            <form-label>{{ i18n("views.DiscountEdit.LeadOperators") }}</form-label>
            <form-field>
                <SwitchInput v-model="leadOperatorsEnabled" />
            </form-field>
        </div>

        <FadeTransition>
            <div class="form-group row" v-if="leadOperatorsEnabled">
                <form-label>{{ i18n("views.DiscountEdit.Mode") }}</form-label>
                <form-field>
                    <EnumSelectBox
                        ref="leadOperatorsModeSelect"
                        v-model="leadOperatorsMode"
                        :enumDataSource="DiscountAccessMode"
                        enumTranslationNamespace="enums.DiscountAccessMode"
                        :optionsFilter="tempDiscountAccessModeOptionsFilterForOperators"
                    />
                </form-field>
            </div>
        </FadeTransition>

        <div class="form-group row">
            <form-label>{{ i18n("views.DiscountEdit.EndUsers") }}</form-label>
            <form-field>
                <SwitchInput v-model="endUsersEnabled" />
            </form-field>
        </div>

        <FadeTransition>
            <div class="form-group row" v-if="endUsersEnabled">
                <form-label>{{ i18n("views.DiscountEdit.Mode") }}</form-label>
                <form-field>
                    <!-- <EnumSelectBox
                        ref="endUsersModeSelect"
                        v-model="endUsersMode"
                        :enumDataSource="DiscountAccessMode"
                        enumTranslationNamespace="enums.DiscountAccessMode"
                        :optionsFilter="endUsersModeOptionsFilter"
                    /> -->
                    <EnumSelectBox
                        ref="endUsersModeSelect"
                        v-model="endUsersMode"
                        :enumDataSource="DiscountAccessMode"
                        enumTranslationNamespace="enums.DiscountAccessMode"
                        :optionsFilter="tempDiscountAccessModeOptionsFilter"
                    />
                </form-field>
            </div>
        </FadeTransition>
    </div>
</template>
<script setup lang="ts">
    import type { IDiscountUserGroupAccessSettings } from "@/data/model/Discount";
    import { DiscountAccessMode } from "@/data/model/Enums";
    import { i18n } from "@/portal/Trans";
    import { onMounted, ref, watch } from "vue";

    import FadeTransition from "@/app/components/transitions/FadeTransition.vue";
    import EntityMultiSelectBox from "@/portal/components/discounts/EntityMultiSelectBox.vue";
    import EnumSelectBox from "@/portal/components/elements/SelectFromEnumInput.vue";
    import SwitchInput from "@/portal/components/elements/SwitchInput.vue";
    import FormField from "@/portal/components/layout/details/FormField.vue";
    import FormLabel from "@/portal/components/layout/details/FormLabel.vue";
    import MainSectionTitle from "@/portal/components/layout/details/MainSectionTitle.vue";

    const props = defineProps<{
        userGroupAccessSettings: IDiscountUserGroupAccessSettings | undefined;
    }>();

    const settings = props.userGroupAccessSettings;
    const operatorsEnabled = ref<boolean>(settings?.operators.enabled ?? false);
    const operatorsMode = ref<DiscountAccessMode | undefined>(settings?.operators.mode);

    const leadOperatorsEnabled = ref<boolean>(settings?.leadOperators.enabled ?? false);
    const leadOperatorsMode = ref<DiscountAccessMode | undefined>(settings?.leadOperators.mode);

    const endUsersEnabled = ref<boolean>(settings?.endUsers.enabled ?? false);
    const endUsersMode = ref<DiscountAccessMode | undefined>(settings?.endUsers.mode);

    const operatorsModeSelect = ref<InstanceType<typeof EntityMultiSelectBox> | null>(null);
    const leadOperatorsModeSelect = ref<InstanceType<typeof EntityMultiSelectBox> | null>(null);
    const endUsersModeSelect = ref<InstanceType<typeof EntityMultiSelectBox> | null>(null);

    watch(operatorsEnabled, (value) => {
        if (!value) {
            operatorsMode.value = undefined;
        }
    });

    watch(leadOperatorsEnabled, (value) => {
        if (!value) {
            leadOperatorsMode.value = undefined;
        }
    });

    watch(endUsersEnabled, (value) => {
        if (!value) {
            endUsersMode.value = undefined;
        }
    });

    function init() {}

    function validate() {
        let isValid = true;
        if (operatorsEnabled.value && operatorsMode.value == undefined) {
            operatorsModeSelect.value?.setError(true);
            isValid = false;
        }

        if (leadOperatorsEnabled.value && leadOperatorsMode.value == undefined) {
            leadOperatorsModeSelect.value?.setError(true);
            isValid = false;
        }

        if (endUsersEnabled.value && endUsersMode.value == undefined) {
            endUsersModeSelect.value?.setError(true);
            isValid = false;
        }

        return isValid;
    }

    function getData(): IDiscountUserGroupAccessSettings | undefined {
        if (!validate()) {
            return undefined;
        }

        return {
            operators: {
                enabled: operatorsEnabled.value,
                mode: operatorsEnabled.value ? operatorsMode.value : undefined,
            },
            leadOperators: {
                enabled: leadOperatorsEnabled.value,
                mode: leadOperatorsEnabled.value ? leadOperatorsMode.value : undefined,
            },
            endUsers: {
                enabled: endUsersEnabled.value,
                mode: endUsersEnabled.value ? endUsersMode.value : undefined,
            },
        };
    }

    // todo: to remove after we implement other modes
    function tempDiscountAccessModeOptionsFilterForOperators(value: number) {
        return value !== DiscountAccessMode.Automatically;
    }

    function tempDiscountAccessModeOptionsFilter(value: number) {
        return (
            value !== DiscountAccessMode.Button &&
            value !== DiscountAccessMode.CodeOrButton &&
            value !== DiscountAccessMode.Automatically
        );
    }

    onMounted(async () => {
        init();
    });

    defineExpose({
        validate,
        getData,
    });
</script>

<style lang="scss"></style>
